import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 39. EVENT BUS
// Système d'événements central. Les modules émettent des événements
// (customer.created, appointment.confirmed, invoice.paid, …).
// Ces événements peuvent déclencher : notifications, workflows,
// marketing, analytics et actions AI.
// ─────────────────────────────────────────────────────────────

export const EVENT_CATEGORIES: { key: string; label: string; icon: string; className: string }[] = [
  { key: 'customer', label: 'Client', icon: 'ri-user-star-line', className: 'bg-secondary-100 text-secondary-900' },
  { key: 'appointment', label: 'Rendez-vous', icon: 'ri-calendar-line', className: 'bg-primary-100 text-primary-900' },
  { key: 'booking', label: 'Réservation', icon: 'ri-hotel-line', className: 'bg-primary-100 text-primary-900' },
  { key: 'order', label: 'Commande', icon: 'ri-shopping-bag-3-line', className: 'bg-accent-100 text-accent-900' },
  { key: 'invoice', label: 'Facture', icon: 'ri-file-list-3-line', className: 'bg-accent-100 text-accent-900' },
  { key: 'payment', label: 'Paiement', icon: 'ri-money-dollar-circle-line', className: 'bg-primary-100 text-primary-900' },
];

export function eventCategoryMeta(key: string | null | undefined) {
  return EVENT_CATEGORIES.find((c) => c.key === key) || { key: 'other', label: 'Autre', icon: 'ri-flashlight-line', className: 'bg-background-100 text-foreground-700' };
}

export interface CoreEventType {
  id: number;
  key: string;
  category: string | null;
  label: string | null;
  description: string | null;
  is_active: boolean | null;
  created_at: string | null;
}

export interface CoreEventLog {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  event_key: string;
  entity_type: string | null;
  entity_id: string | null;
  payload: Record<string, unknown> | null;
  actor_user_id: number | null;
  source: string | null;
  processed: boolean | null;
  created_at: string | null;
}

export async function listEventTypes(): Promise<CoreEventType[]> {
  const { data, error } = await supabase
    .from('core_event_types')
    .select('*')
    .order('category', { ascending: true })
    .order('key', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreEventType[];
}

export async function createEventType(input: {
  key: string;
  category: string;
  label: string;
  description?: string;
}): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_event_types')
    .insert({ key: input.key, category: input.category, label: input.label, description: input.description || null, is_active: true })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function setEventTypeActive(id: number, active: boolean): Promise<void> {
  const { error } = await supabase.from('core_event_types').update({ is_active: active }).eq('id', id);
  if (error) throw error;
}

export async function deleteEventType(id: number): Promise<void> {
  const { error } = await supabase.from('core_event_types').delete().eq('id', id);
  if (error) throw error;
}

export async function listEventLog(
  organizationId: number,
  filter?: { eventKey?: string; limit?: number },
): Promise<CoreEventLog[]> {
  let query = supabase
    .from('core_event_log')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(filter?.limit ?? 100);
  if (filter?.eventKey) query = query.eq('event_key', filter.eventKey);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreEventLog[];
}

export interface EmitEventInput {
  tenantId: number | null;
  organizationId: number;
  eventKey: string;
  entityType?: string | null;
  entityId?: string | number | null;
  payload?: Record<string, unknown> | null;
  actorUserId?: number | null;
  source?: string | null;
}

/** Publie un événement dans l'Event Bus (journalisé). */
export async function emitEvent(input: EmitEventInput): Promise<number | null> {
  const { data, error } = await supabase
    .from('core_event_log')
    .insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      event_key: input.eventKey,
      entity_type: input.entityType || null,
      entity_id: input.entityId != null ? String(input.entityId) : null,
      payload: input.payload ?? null,
      actor_user_id: input.actorUserId ?? null,
      source: input.source || 'manual',
      processed: false,
    })
    .select('id')
    .maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function markEventProcessed(id: number, processed = true): Promise<void> {
  const { error } = await supabase.from('core_event_log').update({ processed }).eq('id', id);
  if (error) throw error;
}

export async function deleteEventLog(id: number): Promise<void> {
  const { error } = await supabase.from('core_event_log').delete().eq('id', id);
  if (error) throw error;
}

export interface EventStats {
  total: number;
  unprocessed: number;
  byCategory: Record<string, number>;
}

export async function fetchEventStats(organizationId: number): Promise<EventStats> {
  const { data } = await supabase
    .from('core_event_log')
    .select('event_key, processed')
    .eq('organization_id', organizationId);
  const byCategory: Record<string, number> = {};
  let unprocessed = 0;
  (data || []).forEach((r) => {
    const row = r as { event_key: string; processed: boolean | null };
    const cat = (row.event_key || '').split('.')[0] || 'other';
    byCategory[cat] = (byCategory[cat] || 0) + 1;
    if (!row.processed) unprocessed += 1;
  });
  return { total: (data || []).length, unprocessed, byCategory };
}